/**
 * One row, in full.
 *
 * The ids come first and are copyable, because they are how this row is
 * retrieved from anywhere else — an API call, a SQL client, another service.
 */

import { useNavigate } from 'react-router-dom'
import { CopyButton, Modal } from '@/shared/components'
import { KeyValueGrid, Value } from '../components'
import type { ObjectRow, ObjectTable } from '../types/brain.types'

export function ObjectRowDialog({
  graphId,
  table,
  row,
  onClose,
}: {
  graphId: string
  table: ObjectTable
  row: ObjectRow
  onClose: () => void
}) {
  const navigate = useNavigate()

  return (
    <Modal
      title={row.label ?? `${table.node_type_key} row`}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Close
          </button>
          {row.entity_id && (
            <button
              className="btn btn-primary"
              onClick={() => {
                onClose()
                navigate(`/brain/${graphId}/records/${row.entity_id}`)
              }}
            >
              Open the record →
            </button>
          )}
        </>
      }
    >
      <div className="stack">
        <div className="stack" style={{ gap: 6 }}>
          <span className="small faint">Identifiers</span>
          <IdRow label="object_id" value={row.object_id} hint="this row" />
          <IdRow label="entity_id" value={row.entity_id} hint="the golden record behind it" />
          <IdRow label="node_type_id" value={row.node_type_id} hint="its table" />
          <IdRow label="graph_id" value={row.graph_id} hint="the graph it belongs to" />
        </div>

        <div>
          <span className="small faint">Values</span>
          <KeyValueGrid
            rows={table.columns.map((column) => [
              column.column_name,
              <Value key={column.column_name} value={row[column.column_name]} />,
            ])}
          />
        </div>

        <div>
          <span className="small faint">Provenance</span>
          <KeyValueGrid
            rows={[
              ['status', <Value key="s" value={row.status} />],
              [
                'quality_score',
                <Value
                  key="q"
                  value={row.quality_score === null ? null : `${Math.round(row.quality_score * 100)}%`}
                />,
              ],
              [
                'source_ids',
                <Value key="src" value={(row.source_ids ?? []).join(', ') || null} />,
              ],
              ['created_at', <Value key="c" value={row.created_at} />],
              ['updated_at', <Value key="u" value={row.updated_at} />],
            ]}
          />
        </div>

        <div className="callout">
          <p className="small" style={{ margin: 0 }}>
            Read this row straight from the API:
          </p>
          <code className="mono small" style={{ display: 'block', marginTop: 4, overflowWrap: 'anywhere' }}>
            GET /api/v1/brain/graphs/{graphId}/objects/{table.node_type_id}/rows/{row.object_id}
          </code>
        </div>
      </div>
    </Modal>
  )
}

function IdRow({
  label,
  value,
  hint,
}: {
  label: string
  value: string | null
  hint: string
}) {
  return (
    <div className="row-tight">
      <span className="mono small" style={{ minWidth: 110 }}>
        {label}
      </span>
      {value ? (
        <>
          <code className="mono small" style={{ overflowWrap: 'anywhere' }}>
            {value}
          </code>
          <CopyButton value={value} label="Copy" />
        </>
      ) : (
        <span className="faint small">—</span>
      )}
      <span className="spacer" />
      <span className="small faint">{hint}</span>
    </div>
  )
}
